// "⏰ Me avise em…" (0.67, texto 05 G2): no menu ⋮ de qualquer item, em qualquer aba.
// Na hora, o celular avisa com o nome do item; tocar no aviso abre o item. NÃO cria tarefa no My Planner.
// Os avisos ficam só neste aparelho (é o aparelho que toca).
import { useSyncExternalStore } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { AbaId } from '../store/types';
import { modNotificacoes } from '../notifications';
import { comoAvisar, modoDaAba, somDoAviso } from './avisos';
import { t } from './i18n';

export type AlvoAviso = { aba: AbaId; id: string; titulo: string; item?: string };   // item: o que abrir na aba (se diferente do id)
type Marcado = AlvoAviso & { quando: string; nid: string };
const CHAVE = 'mp_meavise';
let marcados: Record<string, Marcado> = {};
let versao = 0;
const ouvintes = new Set<() => void>();
const avisar = () => { versao++; ouvintes.forEach(f => f()); };
const k = (a: { aba: AbaId; id: string }) => `${a.aba}:${a.id}`;
const salvar = () => AsyncStorage.setItem(CHAVE, JSON.stringify(marcados)).catch(() => {});

AsyncStorage.getItem(CHAVE).then(s => {
  try { const m = JSON.parse(s ?? '{}') as Record<string, Marcado>; const agora = Date.now();
    marcados = Object.fromEntries(Object.entries(m).filter(([, v]) => new Date(v.quando).getTime() > agora)); avisar(); } catch { /* vazio */ }
}).catch(() => {});

/** O item tem aviso marcado? (mostra o ⏰ pequeno e "Cancelar aviso" no ⋮) */
export function useMeAvise(a?: { aba: AbaId; id: string } | null) {
  useSyncExternalStore(cb => { ouvintes.add(cb); return () => { ouvintes.delete(cb); }; }, () => versao);
  if (!a) return null;
  const m = marcados[k(a)];
  return m && new Date(m.quando).getTime() > Date.now() ? m : null;
}
export const temAviso = (a: { aba: AbaId; id: string }) => { const m = marcados[k(a)]; return !!m && new Date(m.quando).getTime() > Date.now(); };

/** Marca o aviso. Devolve false se o celular não deixar avisar (sem permissão ou no navegador). */
export async function marcarAviso(a: AlvoAviso, quando: Date): Promise<boolean> {
  const N = modNotificacoes();
  if (!N || Platform.OS === 'web') return false;
  try {
    let p = await N.getPermissionsAsync();
    if (!p.granted) p = await N.requestPermissionsAsync();
    if (!p.granted) return false;
    await cancelarAviso(a);
    const como = comoAvisar(quando, modoDaAba(a.aba)) ?? { canal: 'avisos-silencioso', som: false };
    const nid = await N.scheduleNotificationAsync({
      identifier: 'aviso:' + k(a),   // fora dos prefixos que o rescheduleAll refaz (notifications/index.ts)
      content: { title: '⏰ ' + a.titulo, body: t('Você pediu para ser avisado agora.'), sound: somDoAviso(como),
        data: { tipo: 'meavise', aba: a.aba, id: a.item ?? a.id } },
      trigger: { type: N.SchedulableTriggerInputTypes.DATE, date: quando, channelId: como.canal } as any,
    });
    marcados = { ...marcados, [k(a)]: { ...a, quando: quando.toISOString(), nid } };
    salvar(); avisar();
    return true;
  } catch { return false; }
}
export async function cancelarAviso(a: { aba: AbaId; id: string }) {
  const m = marcados[k(a)];
  if (!m) return;
  try { await modNotificacoes()?.cancelScheduledNotificationAsync(m.nid); } catch { /* já tocou */ }
  const { [k(a)]: _x, ...resto } = marcados;
  marcados = resto; salvar(); avisar();
}

// ─── tocar no aviso de uma tarefa do My Planner abre a ficha dela (AcoesProvider registra; lib/push chama) ───
let abrirTarefa: ((id: string) => void) | null = null;
export const definirAbrirTarefa = (f: ((id: string) => void) | null) => { abrirTarefa = f; };
export const abrirTarefaDoAviso = (id: string) => abrirTarefa?.(id);

// ─── escolha do horário (uma tela só para o app todo, aberta pelo ⋮) ───
let pedido: AlvoAviso | null = null;
const ouvPedido = new Set<() => void>();
/** Abre a escolha "15 min, 30 min, 1 h ou Outro" para o item. */
export function pedirMeAvise(a: AlvoAviso) { pedido = a; ouvPedido.forEach(f => f()); }
export function usePedidoMeAvise() {
  return useSyncExternalStore(cb => { ouvPedido.add(cb); return () => { ouvPedido.delete(cb); }; }, () => pedido);
}
export function fecharPedido() { pedido = null; ouvPedido.forEach(f => f()); }
